import type { ReactNode } from "react"

import handoverReceivedIconUrl from "./icons/handover-received.svg"
import resRequestLinkIconUrl from "./icons/resrequest-link.svg"
import transferVehicleGuideIconUrl from "./icons/transfer-vehicle-guide.svg"
import type { DashboardCardData, DashboardCardMovement } from "./DashboardCard.types"
import { IndicatorChip } from "../shared/IndicatorChip"
import { WarningIndicator } from "../shared/WarningIndicator"

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const millisecondsPerDay = 24 * 60 * 60 * 1000

type DashboardCardProps = {
  card: DashboardCardData
  dashboardDate: string
}

export function DashboardCard({ card, dashboardDate }: DashboardCardProps) {
  return (
    <article
      aria-label={`${card.party_name} dashboard card`}
      className="flex h-full min-w-0 flex-col items-start overflow-hidden rounded border border-[#d5d5d5] bg-white font-guest [font-synthesis:none]"
    >
      <MovementBanner dashboardDate={dashboardDate} movement={card.movement} />

      <div className="flex w-full flex-col gap-2.5 px-4 py-3">
        <div className="flex h-6 w-full items-center gap-1.5">
          <h3 className="flex h-full min-w-0 flex-1 flex-col justify-center truncate text-base font-semibold leading-[19px] tracking-normal text-[#2b2b2b]">
            {card.party_name}
          </h3>
          {card.not_invoiced ? <WarningIndicator label="Not Invoiced" title="Not Yet Invoiced - Check with Consultant" /> : null}
          {card.under_review ? <WarningIndicator /> : null}
          {card.resrequest_reservation_url ? (
            <a
              aria-label="Open ResRequest"
              className="grid h-6 w-6 shrink-0 place-items-center rounded text-[#495b6a] hover:bg-[#f6f7f8] hover:text-[#1d262e] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#495b6a]"
              href={card.resrequest_reservation_url}
              rel="noreferrer"
              target="_blank"
              title="ResRequest"
            >
              <img alt="" className="block h-3.5 w-3.5" src={resRequestLinkIconUrl} />
            </a>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-1.5 overflow-hidden bg-[repeating-linear-gradient(to_right,#d5d5d5_0_4px,transparent_4px_9px)] bg-[length:100%_1px] bg-left-bottom bg-repeat-x pb-3">
          <MetaItem label="Guests">
            <GuestSummary
              adults={card.guests.adults}
              childAges={card.child_ages}
              children={card.guests.children}
            />
          </MetaItem>

          {card.date_row ? (
            <MetaItem label={card.date_row.label}>
              {relativeDayLabel(card.date_row.date, dashboardDate)}{" "}
              <span aria-hidden="true">·</span>{" "}
              {formatDate(card.date_row.date)}
            </MetaItem>
          ) : null}
        </div>

        {card.night ? (
          <div className="flex w-full justify-end overflow-hidden bg-[repeating-linear-gradient(to_right,#d5d5d5_0_4px,transparent_4px_9px)] bg-[length:100%_1px] bg-left-bottom bg-repeat-x pb-3">
            <NightCounter night={card.night} />
          </div>
        ) : null}

        {card.chips.length ? (
          <div className="flex h-6 w-full flex-wrap items-start gap-1.5 overflow-hidden">
            {card.chips.map((chip) => (
              <IndicatorChip count={chip.count} key={`${chip.kind}-${chip.count ?? ""}`} kind={chip.kind} />
            ))}
          </div>
        ) : null}
      </div>

      {card.handover_status ? <HandoverBand status={card.handover_status} /> : null}
    </article>
  )
}

function MovementBanner({ dashboardDate, movement }: { dashboardDate: string; movement?: DashboardCardMovement }) {
  const movementParts = movement
    ? [movementTime(movement, dashboardDate), movementDetail(movement)].filter((part) => part.length > 0)
    : []

  return (
    <div className="-mx-px -mt-px flex h-[26px] w-[calc(100%_+_2px)] box-border items-center justify-between gap-3 overflow-hidden border border-[#c2d3ea] bg-[#ddebff] px-3.5 py-1.5 text-left text-[11px] tracking-[0.44px] text-[#687482] [line-height:normal]">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {movement ? (
          <span className="shrink-0 font-bold uppercase text-[#495b6a]">{movementLabel(movement, dashboardDate)}</span>
        ) : null}
        <span className="min-w-0 flex-1 truncate font-medium">
          {movementParts.map((part, index) => (
            <span key={`${part}-${index}`}>
              {index > 0 ? (
                <>
                  {" "}
                  <span aria-hidden="true">·</span>{" "}
                </>
              ) : null}
              {part}
            </span>
          ))}
        </span>
      </div>

      {movement ? (
        <span aria-hidden="true" className="grid h-4 w-4 shrink-0 place-items-center">
          <img alt="" className="block h-[10.67px] w-4" src={transferVehicleGuideIconUrl} />
        </span>
      ) : null}
    </div>
  )
}

function MetaItem({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="w-14 shrink-0 text-[11px] font-normal uppercase tracking-[0.66px] text-[#7e7e7e] [line-height:normal]">
        {label}
      </span>
      <span className="min-w-0 truncate text-[13px] font-medium text-[#2b2b2b] [line-height:normal]">
        {children}
      </span>
    </div>
  )
}

function GuestSummary({
  adults,
  childAges,
  children,
}: {
  adults: number
  childAges: readonly number[]
  children: number
}) {
  return (
    <>
      {pluralise(adults, "Adult", "Adults")} <span aria-hidden="true">·</span>{" "}
      {pluralise(children, "Child", "Children")}
      {childAges.length ? (
        <span className="font-normal text-[#9b9b9b]"> (ages {childAges.join(", ")})</span>
      ) : null}
    </>
  )
}

function HandoverBand({ status }: { status: NonNullable<DashboardCardData["handover_status"]> }) {
  return (
    <div className="flex h-[43px] w-full items-center justify-between gap-3 border-t border-[#d5d5d5] bg-[#f7f7f7] px-4">
      <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.44px] text-[#495b6a] [line-height:normal]">
        HANDOVER NOTES
      </span>
      <span className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[14px] border border-[#6ca030] bg-white py-1 pr-2.5 pl-2 text-[13px] font-medium text-[#335618] [line-height:normal]">
        <img alt="" className="block h-3.5 w-3" src={handoverReceivedIconUrl} />
        {handoverStatusLabel(status)}
      </span>
    </div>
  )
}

function NightCounter({ night }: { night: NonNullable<DashboardCardData["night"]> }) {
  return (
    <div className="flex min-w-24 shrink-0 items-center justify-between gap-2 rounded border border-[#d7d6d6] bg-[#f7f7f7] px-3.5 py-1.5">
      <span className="text-xs font-normal uppercase tracking-[0.48px] text-[#495b6a] [line-height:normal]">
        Night
      </span>
      <span className="text-sm font-medium text-[#2b2b2b] [line-height:normal]">
        {night.current} of {night.total}
      </span>
    </div>
  )
}

function pluralise(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`
}

function relativeDayLabel(targetDate: string, dashboardDate: string) {
  const days = daysBetween(dashboardDate, targetDate)
  if (days === null) return formatDate(targetDate)
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} ago`
  if (days === 0) return "Today"
  if (days === 1) return "In 1 day"

  return `In ${days} days`
}

function movementLabel(movement: DashboardCardMovement, dashboardDate: string) {
  const movementDate = movement.movement_context === "arrival" ? movement.end_at.slice(0, 10) : movement.start_at.slice(0, 10)
  const days = daysBetween(dashboardDate, movementDate)

  if (movement.movement_context === "arrival") {
    return days === 0 ? "Arrives Today" : "Arrives Tomorrow"
  }

  return days === 1 ? "Departs Tomorrow" : "Departs At"
}

function movementTime(movement: DashboardCardMovement, dashboardDate: string) {
  if (movement.movement_context === "arrival" && daysBetween(dashboardDate, movement.end_at.slice(0, 10)) === 1) {
    return ""
  }

  const value = movement.movement_context === "arrival" ? movement.end_at : movement.start_at
  const match = /T(\d{2}):(\d{2})/.exec(value)
  const hours = match?.[1]
  const minutes = match?.[2]

  return hours && minutes ? `${hours}:${minutes}` : ""
}

function movementDetail(movement: DashboardCardMovement) {
  if (movement.movement_context === "arrival" && movement.from_location) return `From ${movement.from_location}`
  if (movement.movement_context === "departure" && movement.to_location) return `To ${movement.to_location}`

  return ""
}

function handoverStatusLabel(status: NonNullable<DashboardCardData["handover_status"]>) {
  switch (status) {
    case "complete":
      return "Complete"
    case "received":
      return "Received"
  }
}

function daysBetween(startDate: string, targetDate: string) {
  const start = dateParts(startDate)
  const target = dateParts(targetDate)
  if (!start || !target) return null

  const startTime = Date.UTC(start.year, start.month - 1, start.day)
  const targetTime = Date.UTC(target.year, target.month - 1, target.day)

  return Math.round((targetTime - startTime) / millisecondsPerDay)
}

function formatDate(value: string) {
  const parts = dateParts(value)
  if (!parts) return value

  const monthName = monthNames[parts.month - 1]
  if (!monthName) return value

  return `${String(parts.day).padStart(2, "0")} ${monthName}`
}

function dateParts(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  if (!year || !month || !day) return null

  return { day, month, year }
}
